<template>
  <article v-if="ctx.subscription" class="subscription-card" :class="{ '-expired': ctx.expired }">
    <header class="subscription-card__header">
      <h1 class="subscription-card__heading">
        {{ $t(`subscriptions.${ctx.subscription.plan}.name`)
        }}<span v-if="ctx.subscription.organization"
          >:
          <router-link
            :to="{
              name: 'settings-organization',
              params: { organizationSlug: ctx.subscription.organization.slug }
            }"
          >
            <strong>{{ ctx.subscription.organization.name }}</strong>
          </router-link>
        </span>
      </h1>

      <div class="flex self-start">
        <router-link
          class="button-icon"
          :to="{
            name: 'subscription-details',
            params: { subscriptionId: ctx.subscription.id }
          }"
        >
          <lucide-icon name="info" />
        </router-link>
        <router-link
          v-if="!ctx.expired && ctx.subscription.editable && ctx.subscription.plan == 'organization'"
          class="button-icon"
          :to="{
            name: 'subscription-modify',
            params: { subscriptionId: ctx.subscription.id }
          }"
        >
          <lucide-icon name="pencil" />
        </router-link>
      </div>
    </header>
    <footer class="subscription-card__footer">
      <div class="column">
        <div>
          <i18n-t
            v-if="ctx.subscription.seats"
            tag="span"
            keypath="labels.countUsers"
            :plural="ctx.subscription.seats"
            scope="global"
          >
            <template #count
              ><strong>{{ ctx.subscription.seats }}</strong></template
            >
          </i18n-t>

          <span v-if="ctx.subscription.nextRenew && ctx.subscription.nextRenew.seats < ctx.subscription.seats" class="color-text-medium">
            — {{  $t("texts.reducedSeats", { date: ctx.formattedRenewalDate, seats: ctx.subscription.nextRenew.seats }) }}
          </span>
        </div>
        <div v-for="upgrade of ctx.subscription.upgrades" :key="upgrade">
          <strong>{{ $t(`upgrades.${upgrade}.name`) }}</strong>
          <span v-if="ctx.subscription.nextRenew && !ctx.subscription.nextRenew.upgrades.includes(upgrade)" class="color-text-medium" :title="$t('texts.removedAt', { date: ctx.formattedRenewalDate })">
            — {{ $t("labels.canceled") }}*
          </span>
        </div>
      </div>
      <div class="self-end">
        <span
          v-if="ctx.subscription.renewAutomatically"
          class="subscription-card__valid-badge -renewing"
        >
          <i18n-t keypath="texts.nextRenewal" scope="global">
            <template #date
              ><strong>{{ ctx.formattedRenewalDate }}</strong></template
            >
          </i18n-t>
        </span>
        <span
          v-else-if="ctx.closeToExpiration"
          class="subscription-card__valid-badge -expiring"
        >
          <i18n-t keypath="texts.expiresAt" scope="global">
            <template #date
              ><strong>{{ ctx.formattedValidUntilDate }}</strong></template
            >
          </i18n-t>
        </span>
        <span
          v-else-if="ctx.expired"
          class="subscription-card__valid-badge -expired"
        >
          <i18n-t keypath="texts.expiredAt" scope="global">
            <template #date
              ><strong>{{ ctx.formattedExpiredAtDate }}</strong></template
            >
          </i18n-t>
        </span>
        <span v-else class="subscription-card__valid-badge -valid">
          <i18n-t keypath="texts.expiresAt" scope="global">
            <template #date
              ><strong>{{ ctx.formattedValidUntilDate }}</strong></template
            >
          </i18n-t>
        </span>
      </div>
    </footer>
  </article>
</template>

<script setup lang="ts">
import LucideIcon from "@core/ui/LucideIcon.vue";

import { useSubscription } from "./useSubscription";

const props = defineProps<{
  subscriptionId: number;
}>();

const ctx = useSubscription(() => props.subscriptionId);
</script>

<style lang="scss">
.subscription-card {
  width: 100%;
  box-shadow: 0 2px 10px var(--color-shadow-1);
  background-color: rgba(255, 255, 255, 0.4);
  border-radius: 5px;
  background-color: var(--color-background-deep);
  overflow: hidden;
  // container
  container: subscription-card / inline-size;

  &.-expired {
    opacity: 0.75;
  }

  &__header,
  &__footer {
    display: flex;
    padding: 1.25rem 1.5rem;
    justify-content: space-between;
    gap: 1rem;
  }
  &__footer {
    flex-direction: column;
  }
  @container (min-width: 600px) {
    &__header,
    &__footer {
      gap: 0;
    }
    &__footer {
      flex-direction: row;
    }
  }

  &__header {
    align-items: center;
  }
  &__heading {
    font-size: 1.25rem;
    margin: 0;
  }

  &__footer {
    background-color: var(--color-background);
  }

  &__valid-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    text-wrap: balance;

    &.-valid {
      background-color: var(--color-warning-light);
      color: var(--color-warning-dark);
    }
    &.-expiring {
      background-color: var(--color-error-light);
      color: var(--color-error-dark);
    }
    &.-expired {
      background-color: var(--color-grey-light);
      color: var(--color-text-medium);
    }
    &.-renewing {
      background-color: var(--color-success-light);
      color: var(--color-success-dark);
    }
  }
}
</style>
